import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { verifySignupEmailApi, resendSignupVerificationApi } from "../../api/client";
import { useTheme } from "@mui/material/styles";
import { useBrand } from "../../context/BrandContext";
import { readStoredLocale, setUiLocale } from "../../i18n/config";
import { parseUiLocaleFromSearch } from "../../i18n/urlLocale";
import { getLoginUiStrings } from "../../i18n/loginUiStrings";

function readVerifyLocale() {
  if (typeof window !== "undefined") {
    const fromUrl = parseUiLocaleFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
  }
  return readStoredLocale() || "pt-BR";
}

const VerifyEmailPage = () => {
  const brand = useBrand();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const scrollRef = useRef(null);

  const token = String(searchParams.get("t") || "").trim();
  const companyFromLink = String(searchParams.get("c") || "").trim().toLowerCase();

  const [localeChoice] = useState(readVerifyLocale);
  const [status, setStatus] = useState(token ? "working" : "need_token");
  const [error, setError] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendWorkspace, setResendWorkspace] = useState(companyFromLink);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendOk, setResendOk] = useState(false);

  const L = getLoginUiStrings(localeChoice);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus("need_token");
      return;
    }
    let cancelled = false;
    (async () => {
      setStatus("working");
      setError(null);
      try {
        const out = await verifySignupEmailApi(token);
        if (cancelled) return;
        setStatus("success");
        if (out?.company) {
          try {
            localStorage.setItem("company", out.company);
          } catch {
            /* ignore */
          }
        }
        setUiLocale(localeChoice);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        const Lnow = getLoginUiStrings(localeChoice);
        setError(e?.message || Lnow.verifyEmailInvalid);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, localeChoice]);

  const handleResend = async () => {
    const em = String(resendEmail || "").trim().toLowerCase();
    const ws = String(resendWorkspace || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError(L.invalidEmail);
      return;
    }
    if (ws.length < 2) {
      setError(L.signupWorkspaceHint);
      return;
    }
    setResendBusy(true);
    setError(null);
    setResendOk(false);
    try {
      await resendSignupVerificationApi({ email: em, company: ws });
      setResendOk(true);
    } catch (e) {
      setError(e?.message || L.verifyResendError);
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <Box
      ref={scrollRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #121212, #1e1e1e)"
            : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        p: 2,
        pt: 4,
        pb: 4,
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center" mb={4} flexShrink={0}>
        <img
          src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
          alt={brand.name}
          style={{ height: 60 }}
          loading="lazy"
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 440,
          width: "90%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <MarkEmailReadOutlinedIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />

        <Typography variant="h5" gutterBottom align="center">
          {L.verifyEmailTitle}
        </Typography>

        {status === "working" && (
          <Box display="flex" alignItems="center" gap={2} py={3}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              {L.verifyEmailWorking}
            </Typography>
          </Box>
        )}

        {status === "success" && (
          <Alert severity="success" sx={{ width: "100%", my: 2 }}>
            {L.verifyEmailSuccess}
          </Alert>
        )}

        {status === "error" && error && (
          <Alert severity="error" sx={{ width: "100%", my: 2 }} role="alert">
            {error}
          </Alert>
        )}

        {status === "need_token" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
            {L.verifyEmailNeedLink}
          </Typography>
        )}

        {(status === "success" || status === "error" || status === "need_token") && status !== "working" && (
          <Box width="100%" mt={1}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {L.verifyResendSection}
            </Typography>
            <TextField
              label={L.email}
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              autoComplete="email"
            />
            <TextField
              label={L.workspaceField}
              value={resendWorkspace}
              onChange={(e) => setResendWorkspace(String(e.target.value || "").trim().toLowerCase())}
              fullWidth
              sx={{ mb: 2 }}
              spellCheck={false}
            />
            {resendOk && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {L.verifyResendSent}
              </Alert>
            )}
            <Button
              variant="outlined"
              fullWidth
              onClick={handleResend}
              disabled={resendBusy}
              startIcon={resendBusy && <CircularProgress size={18} />}
            >
              {resendBusy ? L.sending : L.verifyResendCta}
            </Button>
          </Box>
        )}

        <Typography variant="body2" sx={{ mt: 3, color: theme.palette.text.secondary, textAlign: "center" }}>
          <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: "bold" }}>
            {L.signInInstead}
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default VerifyEmailPage;
